import React, { useCallback, useEffect, useMemo, useState } from "react";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { getCurrentUser } from "../utils/auth";
import { usePermissions } from "../utils/permissions";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const SUMMARY_ENDPOINT = "/api/containers/dashboard/summary";
const BREAKDOWN_ENDPOINT = "/api/containers/dashboard/breakdown";
const TREND_ENDPOINT = "/api/containers/dashboard/trend";
const DEFAULT_DATE_FIELD = "eta";
const DEFAULT_FILTERS = {
  date_field: DEFAULT_DATE_FIELD,
  date_from: "",
  date_to: "",
  cust_code: "",
  status: "",
  destination: "",
  product_code: "",
  retail_sku: "",
};
const DATE_FIELD_LABELS = {
  eta: "ETA",
  etd: "ETD",
  arrived: "Arrived",
  appt_date: "Appointment Date",
  delivered_dt: "Delivered Date",
  emptied_dt: "Emptied Date",
};
const FALLBACK_DATE_FIELDS = ["eta", "etd", "arrived", "appt_date", "delivered_dt", "emptied_dt"];
const STATUS_OPTIONS = [
  "Planning",
  "Booked",
  "Offshore",
  "Arrival",
  "Appt Made",
  "Delivered",
  "Emptied",
  "Closed",
];
const TREND_GRAIN_OPTIONS = [
  { value: "date", label: "Date" },
  { value: "month", label: "Month" },
  { value: "quarter", label: "Quarter" },
  { value: "year", label: "Year" },
];
const DATE_FIELD_TOOLTIP =
  "Date Field determines which container date is used for the Date From / Date To filter and the trend timeline.";

function buildAuthHeaders(user) {
  const headers = {};
  if (user?.email) headers["X-User-Email"] = user.email;
  if (user?.role) headers["X-User-Role"] = user.role || "";
  return headers;
}

function buildQueryString(filters) {
  const params = new URLSearchParams();
  params.set("date_field", filters.date_field || DEFAULT_DATE_FIELD);

  const mappings = [
    ["date_from", filters.date_from],
    ["date_to", filters.date_to],
    ["cust_code", filters.cust_code],
    ["status", filters.status],
    ["destination", filters.destination],
    ["product_code", filters.product_code],
    ["retail_sku", filters.retail_sku],
  ];

  mappings.forEach(([key, value]) => {
    const trimmed = String(value || "").trim();
    if (trimmed) params.set(key, trimmed);
  });

  return params.toString();
}

function createHttpError(status, payload) {
  const error = new Error(payload?.error || payload?.message || `Request failed (${status})`);
  error.status = status;
  error.payload = payload;
  return error;
}

function normaliseFilters(filters) {
  return {
    ...DEFAULT_FILTERS,
    ...filters,
    date_field: filters?.date_field || DEFAULT_DATE_FIELD,
  };
}

function formatNumber(value, digits = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(parsed);
}

function formatDateFieldLabel(value) {
  return DATE_FIELD_LABELS[value] || String(value || "").replace(/_/g, " ");
}

function formatDateValue(value) {
  if (!value) return "-";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function normaliseLabel(value) {
  const text = String(value || "").trim();
  if (!text || text === "__MISSING__") return "-";
  return text;
}

function resolveDashboardError(error) {
  const status = error?.status;
  const code = error?.payload?.error || error?.message || "";
  if (status === 401 || code === "auth_required") {
    return "Your session is no longer active. Please sign in again.";
  }
  if (status === 403 || code === "page_not_allowed") {
    return "You do not have access to Container Dashboard.";
  }
  if (status === 404 || code === "user_not_found") {
    return "Your user profile could not be resolved. Please contact an administrator.";
  }
  if (code === "invalid_date_range") {
    return "Date From must be on or before Date To.";
  }
  if (code === "invalid_date_field") {
    return "The selected date field is not supported.";
  }
  return error?.message || "Unable to load container dashboard.";
}

function sortTrendSeries(series = []) {
  return [...series].sort((left, right) => String(left?.date || "").localeCompare(String(right?.date || "")));
}

function parseTrendDate(value) {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatTrendBucketLabel(value, grain) {
  const parsed = parseTrendDate(value);
  if (!parsed) return String(value || "-");

  if (grain === "year") {
    return String(parsed.getFullYear());
  }

  if (grain === "quarter") {
    const quarter = Math.floor(parsed.getMonth() / 3) + 1;
    return `Q${quarter} ${parsed.getFullYear()}`;
  }

  if (grain === "month") {
    return parsed.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
    });
  }

  return formatDateValue(value);
}

function buildTrendBucket(value, grain) {
  const parsed = parseTrendDate(value);
  if (!parsed) {
    return { key: String(value || "-"), sortKey: String(value || "-"), label: String(value || "-") };
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");

  if (grain === "year") {
    return {
      key: String(year),
      sortKey: String(year),
      label: formatTrendBucketLabel(value, grain),
    };
  }

  if (grain === "quarter") {
    const quarter = Math.floor(parsed.getMonth() / 3) + 1;
    return {
      key: `${year}-Q${quarter}`,
      sortKey: `${year}-${quarter}`,
      label: formatTrendBucketLabel(value, grain),
    };
  }

  if (grain === "month") {
    return {
      key: `${year}-${month}`,
      sortKey: `${year}-${month}`,
      label: formatTrendBucketLabel(value, grain),
    };
  }

  return {
    key: `${year}-${month}-${day}`,
    sortKey: `${year}-${month}-${day}`,
    label: formatTrendBucketLabel(value, grain),
  };
}

function regroupTrendSeries(series = [], grain = "date") {
  const sortedSeries = sortTrendSeries(series);
  if (grain === "date") {
    return sortedSeries.map((row) => ({
      ...row,
      label: formatTrendBucketLabel(row?.date, grain),
    }));
  }

  const grouped = new Map();
  sortedSeries.forEach((row) => {
    const bucket = buildTrendBucket(row?.date, grain);
    const current = grouped.get(bucket.key) || {
      date: bucket.key,
      label: bucket.label,
      sortKey: bucket.sortKey,
      container_count: 0,
      units: 0,
    };
    current.container_count += Number(row?.container_count) || 0;
    current.units += Number(row?.units) || 0;
    grouped.set(bucket.key, current);
  });

  return [...grouped.values()]
    .sort((left, right) => String(left.sortKey).localeCompare(String(right.sortKey)))
    .map(({ sortKey, ...row }) => row);
}

function StatusPill({ label, count }) {
  return (
    <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs text-slate-700">
      <span className="font-medium">{label}</span>
      <span className="ml-2 tabular-nums text-slate-500">{formatNumber(count)}</span>
    </div>
  );
}

function SummaryCard({ label, value, helper, children }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">{value}</div>
      {helper ? <div className="mt-1 text-xs text-slate-500">{helper}</div> : null}
      {children ? <div className="mt-4">{children}</div> : null}
    </section>
  );
}

function BreakdownPanel({ title, subtitle, rows, columns, emptyMessage, limit = null }) {
  const visibleRows = limit ? rows.slice(0, limit) : rows;
  const hiddenCount = limit && rows.length > limit ? rows.length - limit : 0;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        <div className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
          {formatNumber(rows.length)}
        </div>
      </div>

      {visibleRows.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-3 py-2 font-semibold ${column.align === "right" ? "text-right" : "text-left"}`}
                  >
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={`${title}-${row?.id || row?.label || row?.product_code || index}`} className="border-b border-slate-100 last:border-b-0">
                  {columns.map((column) => {
                    const rawValue = typeof column.render === "function" ? column.render(row) : row?.[column.key];
                    return (
                      <td
                        key={column.key}
                        className={`px-3 py-2 align-top text-slate-700 ${column.align === "right" ? "text-right tabular-nums" : "text-left"}`}
                      >
                        {rawValue}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          {emptyMessage}
        </div>
      )}

      {hiddenCount > 0 ? (
        <div className="mt-3 text-xs text-slate-500">Showing top {formatNumber(visibleRows.length)} rows. {formatNumber(hiddenCount)} more not shown.</div>
      ) : null}
    </section>
  );
}

function TrendPanel({ trend, loading, grain, onGrainChange }) {
  const series = Array.isArray(trend?.series) ? trend.series : [];
  const maxContainers = series.reduce((max, row) => Math.max(max, Number(row?.container_count) || 0), 0);
  const activeGrainLabel = TREND_GRAIN_OPTIONS.find((option) => option.value === grain)?.label || "Date";

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Trend</h2>
          <p className="mt-1 text-sm text-slate-500">
            Time series by {formatDateFieldLabel(trend?.date_field || DEFAULT_DATE_FIELD)} with {activeGrainLabel.toLowerCase()} buckets.
          </p>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            {TREND_GRAIN_OPTIONS.map((option) => {
              const active = option.value === grain;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => onGrainChange(option.value)}
                  disabled={loading}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
                    active ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
                  } ${loading ? "cursor-not-allowed opacity-50" : ""}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div className="text-right text-sm text-slate-500">
            <div>Containers: <span className="font-medium tabular-nums text-slate-700">{formatNumber(trend?.totals?.container_count || 0)}</span></div>
            <div>Units: <span className="font-medium tabular-nums text-slate-700">{formatNumber(trend?.totals?.units || 0)}</span></div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Loading trend...
        </div>
      ) : series.length ? (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2 font-semibold">{activeGrainLabel}</th>
                <th className="px-3 py-2 font-semibold text-right">Containers</th>
                <th className="px-3 py-2 font-semibold">Volume</th>
                <th className="px-3 py-2 font-semibold text-right">Units</th>
              </tr>
            </thead>
            <tbody>
              {series.map((row) => {
                const containerCount = Number(row?.container_count) || 0;
                const width = maxContainers > 0 ? `${Math.max((containerCount / maxContainers) * 100, 3)}%` : "0%";
                return (
                  <tr key={`${row?.date || "na"}-${containerCount}`} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-2 text-slate-700">{row?.label || formatTrendBucketLabel(row?.date, grain)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatNumber(containerCount)}</td>
                    <td className="px-3 py-2">
                      <div className="h-2.5 w-full rounded-full bg-slate-100">
                        <div className="h-2.5 rounded-full bg-blue-500" style={{ width }} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{formatNumber(row?.units || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          No trend data is available for the current filters.
        </div>
      )}
    </section>
  );
}

export default function MrpContainerDashboard() {
  const me = getCurrentUser();
  const userEmail = me?.email || "";
  const userRole = me?.role || "";
  const { getLevel } = usePermissions();
  const permissionLevel = getLevel(userRole, "mrp-container-details");
  const [draftFilters, setDraftFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [dashboardData, setDashboardData] = useState(null);
  const [supportedDateFields, setSupportedDateFields] = useState(FALLBACK_DATE_FIELDS);
  const [resolvedRole, setResolvedRole] = useState("");
  const [trendGrain, setTrendGrain] = useState("date");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDashboard = useCallback(async (filters, signal) => {
    const headers = buildAuthHeaders({ email: userEmail, role: userRole });
    const query = buildQueryString(filters);
    const urls = [SUMMARY_ENDPOINT, BREAKDOWN_ENDPOINT, TREND_ENDPOINT].map((path) =>
      `${API_BASE}${path}?${query}`
    );

    const responses = await Promise.all(urls.map((url) => fetch(url, { headers, signal })));
    const payloads = await Promise.all(responses.map((resp) => resp.json().catch(() => ({}))));

    responses.forEach((resp, index) => {
      if (!resp.ok || payloads[index]?.ok === false) {
        throw createHttpError(resp.status, payloads[index]);
      }
    });

    const summaryPayload = payloads[0] || {};
    const breakdownPayload = payloads[1] || {};
    const trendPayload = payloads[2] || {};

    return {
      summary: summaryPayload.summary || {
        total_containers: 0,
        total_units: 0,
        by_status_counts: [],
        by_destination_counts: [],
        by_cust_code_counts: [],
        unit_field: "pieces",
      },
      breakdowns: breakdownPayload.breakdowns || {
        status_distribution: [],
        destination_distribution: [],
        cust_code_distribution: [],
        product_distribution: [],
      },
      trend: {
        date_field: trendPayload?.trend?.date_field || filters.date_field || DEFAULT_DATE_FIELD,
        totals: trendPayload?.trend?.totals || { container_count: 0, units: 0 },
        series: sortTrendSeries(trendPayload?.trend?.series || []),
      },
      filters:
        summaryPayload.filters ||
        breakdownPayload.filters ||
        trendPayload.filters || {
          supported_date_fields: FALLBACK_DATE_FIELDS,
        },
      role: summaryPayload.role || breakdownPayload.role || trendPayload.role || "",
    };
  }, [userEmail, userRole]);

  useEffect(() => {
    if (permissionLevel === "None") {
      setDashboardData(null);
      setResolvedRole("");
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);
    setError("");
    setDashboardData(null);
    setResolvedRole("");

    fetchDashboard(appliedFilters, controller.signal)
      .then((payload) => {
        setDashboardData(payload);
        setSupportedDateFields(payload?.filters?.supported_date_fields || FALLBACK_DATE_FIELDS);
        setResolvedRole(payload?.role || "");
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        setDashboardData(null);
        setSupportedDateFields(FALLBACK_DATE_FIELDS);
        setResolvedRole("");
        setError(resolveDashboardError(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [appliedFilters, fetchDashboard, permissionLevel]);

  const updateDraftFilter = useCallback((key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const applyFilters = useCallback(
    (event) => {
      if (event?.preventDefault) event.preventDefault();
      setAppliedFilters(normaliseFilters(draftFilters));
    },
    [draftFilters]
  );

  const resetFilters = useCallback(() => {
    const resetValue = { ...DEFAULT_FILTERS };
    setDraftFilters(resetValue);
    setAppliedFilters(resetValue);
  }, []);

  const dateFieldOptions = useMemo(() => {
    const unique = Array.from(new Set([...(supportedDateFields || []), DEFAULT_DATE_FIELD]));
    return unique;
  }, [supportedDateFields]);

  const statusSummary = dashboardData?.summary?.by_status_counts || [];
  const breakdowns = dashboardData?.breakdowns || {};
  const groupedTrendSeries = useMemo(
    () => regroupTrendSeries(dashboardData?.trend?.series || [], trendGrain),
    [dashboardData?.trend?.series, trendGrain]
  );
  const trend = useMemo(
    () => ({
      date_field: dashboardData?.trend?.date_field || appliedFilters.date_field,
      totals: dashboardData?.trend?.totals || { container_count: 0, units: 0 },
      series: groupedTrendSeries,
    }),
    [appliedFilters.date_field, dashboardData?.trend?.date_field, dashboardData?.trend?.totals, groupedTrendSeries]
  );
  const hasAnyData = Boolean(dashboardData) && (
    Number(dashboardData?.summary?.total_containers || 0) > 0 ||
    (dashboardData?.trend?.series || []).length > 0
  );

  if (permissionLevel === "None") {
    return (
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">Container Dashboard</h1>
          <UserAccessSummary />
        </div>
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          You do not have access to Container Dashboard.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold text-slate-900">Container Dashboard</h1>
            {resolvedRole ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                Role: {resolvedRole}
              </span>
            ) : null}
          </div>
          <p className="text-sm text-slate-500">
            Internal container aggregations for operational monitoring. Filters are applied server-side.
          </p>
        </div>
        <UserAccessSummary />
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
            <p className="mt-1 text-sm text-slate-500">Apply filters once to refresh summary, breakdown, and trend together.</p>
          </div>
          <div className="text-xs text-slate-500">Date field default: {formatDateFieldLabel(DEFAULT_DATE_FIELD)}</div>
        </div>

        <form onSubmit={applyFilters} className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label className="space-y-1 text-sm text-slate-700">
              <span className="flex items-center gap-2 font-medium">
                <span>Date Field</span>
                <span
                  title={DATE_FIELD_TOOLTIP}
                  aria-label={DATE_FIELD_TOOLTIP}
                  className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full border border-slate-300 text-[10px] font-semibold text-slate-500"
                >
                  i
                </span>
              </span>
              <select
                value={draftFilters.date_field}
                onChange={(event) => updateDraftFilter("date_field", event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {dateFieldOptions.map((field) => (
                  <option key={field} value={field}>
                    {formatDateFieldLabel(field)}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Date From</span>
              <input
                type="date"
                value={draftFilters.date_from}
                onChange={(event) => updateDraftFilter("date_from", event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Date To</span>
              <input
                type="date"
                value={draftFilters.date_to}
                onChange={(event) => updateDraftFilter("date_to", event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Status</span>
              <select
                value={draftFilters.status}
                onChange={(event) => updateDraftFilter("status", event.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Cust Code</span>
              <input
                type="text"
                value={draftFilters.cust_code}
                onChange={(event) => updateDraftFilter("cust_code", event.target.value)}
                placeholder="e.g. WLGBL"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Destination</span>
              <input
                type="text"
                value={draftFilters.destination}
                onChange={(event) => updateDraftFilter("destination", event.target.value)}
                placeholder="e.g. Long Beach,CA"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Product Code</span>
              <input
                type="text"
                value={draftFilters.product_code}
                onChange={(event) => updateDraftFilter("product_code", event.target.value)}
                placeholder="e.g. BD-200C-E-RT"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>

            <label className="space-y-1 text-sm text-slate-700">
              <span className="font-medium">Retail SKU</span>
              <input
                type="text"
                value={draftFilters.retail_sku}
                onChange={(event) => updateDraftFilter("retail_sku", event.target.value)}
                placeholder="e.g. 673934497"
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </label>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="submit"
              className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-1"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-1"
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-500 shadow-sm">
          Loading container dashboard...
        </div>
      ) : null}

      {!loading && !error ? (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
            <SummaryCard
              label="Total Containers"
              value={formatNumber(dashboardData?.summary?.total_containers || 0)}
              helper="Server-side aggregated container count."
            />
            <SummaryCard
              label="Total Units"
              value={formatNumber(dashboardData?.summary?.total_units || 0)}
              helper="Operational units based on pieces."
            />
            <SummaryCard
              label="Status Summary"
              value={formatNumber(statusSummary.reduce((sum, row) => sum + (Number(row?.count) || 0), 0))}
              helper="Top status counts for the current filter set."
            >
              <div className="flex flex-wrap gap-2">
                {statusSummary.length ? (
                  statusSummary.map((row) => (
                    <StatusPill key={row?.status || row?.label} label={normaliseLabel(row?.label || row?.status)} count={row?.count || 0} />
                  ))
                ) : (
                  <div className="text-sm text-slate-500">No status data</div>
                )}
              </div>
            </SummaryCard>
          </div>

          {!hasAnyData ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
              No data matches the current filters.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
                <BreakdownPanel
                  title="Status Distribution"
                  subtitle="Container counts by status."
                  rows={breakdowns.status_distribution || []}
                  columns={[
                    { key: "label", label: "Status", render: (row) => normaliseLabel(row?.label || row?.status) },
                    { key: "count", label: "Containers", align: "right", render: (row) => formatNumber(row?.count || 0) },
                  ]}
                  emptyMessage="No status distribution is available."
                />
                <BreakdownPanel
                  title="Destination Distribution"
                  subtitle="Container counts by destination."
                  rows={breakdowns.destination_distribution || []}
                  columns={[
                    { key: "label", label: "Destination", render: (row) => normaliseLabel(row?.label || row?.destination) },
                    { key: "count", label: "Containers", align: "right", render: (row) => formatNumber(row?.count || 0) },
                  ]}
                  emptyMessage="No destination distribution is available."
                />
                <BreakdownPanel
                  title="Cust Code Distribution"
                  subtitle="Current tenant-scoped customer counts."
                  rows={breakdowns.cust_code_distribution || []}
                  columns={[
                    { key: "label", label: "Cust Code", render: (row) => normaliseLabel(row?.label || row?.cust_code) },
                    { key: "count", label: "Containers", align: "right", render: (row) => formatNumber(row?.count || 0) },
                  ]}
                  emptyMessage="No cust code distribution is available."
                />
                <BreakdownPanel
                  title="Product Distribution"
                  subtitle="Top products by aggregated units."
                  rows={breakdowns.product_distribution || []}
                  limit={12}
                  columns={[
                    { key: "product_code", label: "Product Code", render: (row) => normaliseLabel(row?.product_code) },
                    { key: "retail_sku", label: "Retail SKU", render: (row) => normaliseLabel(row?.retail_sku) },
                    { key: "container_count", label: "Containers", align: "right", render: (row) => formatNumber(row?.container_count || 0) },
                    { key: "units", label: "Units", align: "right", render: (row) => formatNumber(row?.units || 0) },
                  ]}
                  emptyMessage="No product distribution is available."
                />
              </div>

              <TrendPanel trend={trend} loading={false} grain={trendGrain} onGrainChange={setTrendGrain} />
            </>
          )}
        </>
      ) : null}
    </div>
  );
}
